import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarRange,
  Plus,
  Trophy,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

import { getPaymentMetrics, PaymentKeys } from "@/api/payments";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import {
  ClientsKeys,
  getReferredClients,
} from "@/features/clients/api/clients";
import {
  getMentorLicensees,
  MentorLicenseesKeys,
} from "@/features/founders/api/mentor-founder";
import EarningsMetricCard from "@/features/founders/components/EarningsMetricCard";
import MyPayoutsTable from "@/features/mentors/components/MyPayoutsTable";
import ReferralsTable from "@/features/mentors/components/ReferralsTable";
import ReferralSubmissionForm from "@/features/mentors/components/ReferralSubmissionForm";
import { formatCurrency } from "@/utils/currencies";

const MentorDashboard = () => {
  // Payout dialog state
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);

  // Fetch mentor licensees for Active Founders metric
  const { data: mentorLicensees, isLoading: isLoadingLicensees } = useQuery({
    queryKey: [MentorLicenseesKeys.GET_LICENSEES],
    queryFn: () => getMentorLicensees(),
  });

  // Fetch payment metrics
  const { data: paymentMetrics, isLoading: isLoadingPayments } = useQuery({
    queryKey: [PaymentKeys.GET_METRICS],
    queryFn: () => getPaymentMetrics(),
  });

  // Fetch referred clients to compute total referrals
  const { data: referredClients = [] } = useQuery({
    queryKey: [ClientsKeys.ReferredClients],
    queryFn: () => getReferredClients(),
  });

  // Calculate KPIs
  const totalFounders = mentorLicensees?.total || 0;

  // Get payment metrics from API
  const availablePayout = paymentMetrics?.availablePayout || 0;
  const monthlyEarningsToDate = paymentMetrics?.thisMonthEarnings || 0;
  const mentorLifetimeEarnings = paymentMetrics?.lifetimeEarnings || 0;

  // Total referrals from API
  const totalReferrals = referredClients.length;

  // Handler for successful payout to refetch payment metrics
  const handlePayoutSuccess = () => {
    // Refetch payment metrics after successful payout
    // The backend should update the available payout amount
  };

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Typography size="h1" className="text-[#1e1e24]">
              Mentor Dashboard
            </Typography>
            <Typography size="b2" className="text-[#767677] mt-0">
              Track your founder relationships and monitor your revenue share
            </Typography>
          </div>

          <div className="flex items-center gap-3">
            {/* {availablePayout > 0 && (
              <Button
                onClick={() => setShowPayoutDialog(true)}
                variant="outline"
                className="whitespace-nowrap"
              >
                Payout
              </Button>
            )} */}

            <ReferralSubmissionForm>
              <Button
                variant="ghost"
                className="h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Lead
              </Button>
            </ReferralSubmissionForm>
          </div>
        </div>

        {/* Overview */}
        <div className="mt-16 mb-16 space-y-4">
          <Typography size="h2" className="text-[#1e1e24]">
            Overview
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 2xl:grid-cols-5 gap-4">
            <EarningsMetricCard
              title="Active Founders"
              value={totalFounders}
              isLoading={isLoadingLicensees}
              icon={<Users className="h-[18px] w-[18px]" />}
              className="md:col-span-2 2xl:col-span-1"
            />
            <EarningsMetricCard
              title="Available Payout"
              value={formatCurrency(availablePayout)}
              isLoading={isLoadingPayments}
              icon={<Wallet className="h-[18px] w-[18px]" />}
              className="md:col-span-2 2xl:col-span-1"
            />
            <EarningsMetricCard
              title="This Month's Earnings"
              value={formatCurrency(monthlyEarningsToDate)}
              isLoading={isLoadingPayments}
              icon={<CalendarRange className="h-[18px] w-[18px]" />}
              className="md:col-span-2 2xl:col-span-1"
            />
            <EarningsMetricCard
              title="Total Lifetime Earnings"
              value={formatCurrency(mentorLifetimeEarnings)}
              isLoading={isLoadingPayments}
              icon={<Trophy className="h-[18px] w-[18px]" />}
              className="md:col-span-3 2xl:col-span-1"
            />
            <EarningsMetricCard
              title="Total Referrals"
              value={totalReferrals}
              icon={<UserPlus className="h-[18px] w-[18px]" />}
              className="md:col-span-3 2xl:col-span-1"
            />
          </div>
        </div>

        {/* Referrals */}
        <div className="space-y-4">
          <Typography size="h2" className="text-[#1e1e24]">
            Referrals
          </Typography>
          <div className="bg-[#f9f9f9] rounded-2xl p-6">
            <Typography size="l2" className="font-semibold text-[#767677]">
              Directory
            </Typography>
            <div className="bg-[#fdfdfd] p-2 mt-4">
              <ReferralsTable />
            </div>
          </div>
        </div>

        {/* Payouts */}
        <div className="mt-16 space-y-4">
          <Typography size="h2" className="text-[#1e1e24]">
            Payouts
          </Typography>
          <div className="bg-[#f9f9f9] rounded-2xl p-6">
            <Typography size="l2" className="font-semibold text-[#767677]">
              Payout History
            </Typography>
            <div className="bg-[#fdfdfd] p-2 mt-4">
              <MyPayoutsTable />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MentorDashboard;
